import React, { useState } from 'react';
import { X, Bell, Send, CheckCircle } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

interface PendingReminder {
    plan: any;
    installment: any;
    type: '3_days' | '1_day' | 'same_day';
    diffDays: number;
}

interface PaymentReminderModalProps {
    isOpen: boolean;
    onClose: () => void;
    reminders: PendingReminder[];
    onComplete: () => void; // Called after sending to refresh history
}

const PaymentReminderModal: React.FC<PaymentReminderModalProps> = ({ isOpen, onClose, reminders, onComplete }) => {
    const { user } = useAuth();
    const [sending, setSending] = useState(false);
    const [progress, setProgress] = useState(0);
    const [results, setResults] = useState<{ success: number; failed: number }>({ success: 0, failed: 0 });

    if (!isOpen) return null;

    const handleSendAll = async () => {
        if (!await window.appConfirm(`${reminders.length} adet hatırlatma SMS'i gönderilecek. Onaylıyor musunuz?`)) return;

        setSending(true);
        setResults({ success: 0, failed: 0 });
        let successCount = 0;
        let failedCount = 0;

        for (let i = 0; i < reminders.length; i++) {
            const item = reminders[i];
            const { plan, installment, type } = item;

            // Prepare Message
            const studentName = `${plan.student.first_name} ${plan.student.last_name}`;
            const amount = Number(installment.amount).toLocaleString('tr-TR');
            const date = new Date(installment.date).toLocaleDateString('tr-TR');
            const phone = plan.student.parent_phone || plan.student.phone; // Fallback to student phone if parent missing

            if (!phone) {
                failedCount++;
                continue;
            }

            const dayText = type === '3_days' ? '3 gün' : type === '1_day' ? '1 gün' : 'bugün';
            const message = type === 'same_day'
                ? `Sayın Veli, ${studentName} isimli öğrencimizin ${date} tarihli ${amount} ${plan.currency} tutarındaki taksit ödemesi bugün gerçekleştirilmelidir. Bilgilerinize sunarız.`
                : `Sayın Veli, ${studentName} isimli öğrencimizin ${date} tarihli ${amount} ${plan.currency} tutarındaki taksit ödemesinin vadesine ${dayText} kalmıştır. Bilgilerinize sunarız.`;

            try {
                // 1. Send SMS
                const { error: smsError } = await supabase.functions.invoke('send-sms', {
                    body: {
                        phones: [phone],
                        message,
                        userId: user?.id,
                        branchId: user?.branchId
                    }
                });

                if (smsError) throw smsError;

                // 2. Log to DB
                const { error: dbError } = await supabase
                    .from('testprep_payment_reminders')
                    .insert({
                        payment_plan_id: plan.id,
                        installment_date: installment.date,
                        reminder_type: type,
                        sent_to: phone,
                        message_content: message
                    });

                if (dbError) throw dbError;

                successCount++;
            } catch (err) {
                console.error(err);
                failedCount++;
            }

            // Update progress
            setResults({ success: successCount, failed: failedCount });
            setProgress(Math.round(((i + 1) / reminders.length) * 100));
        }

        setSending(false);
        setTimeout(() => {
            onComplete();
            onClose();
            alert(`İşlem tamamlandı. Başarılı: ${successCount}, Hatalı: ${failedCount}`);
        }, 1000);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col shadow-2xl">
                <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                        <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg">
                            <Bell className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Ödeme Hatırlatıcıları</h2>
                            <p className="text-sm text-gray-500">Yaklaşan ödemeler için otomatik SMS listesi</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    {sending ? (
                        <div className="flex flex-col items-center justify-center py-12 space-y-4">
                            <div className="w-full max-w-md bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                                <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" style={{ width: `${progress}%` }}></div>
                            </div>
                            <p className="text-gray-600 dark:text-gray-400 font-medium">Gönderiliyor... %{progress}</p>
                            <div className="flex gap-4 text-sm">
                                <span className="text-green-600">Başarılı: {results.success}</span>
                                <span className="text-red-600">Hatalı: {results.failed}</span>
                            </div>
                        </div>
                    ) : reminders.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                            <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                            <p>Gönderilecek yeni hatırlatma bulunmuyor.</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {reminders.map((item, idx) => (
                                <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-800">
                                    <div className="flex items-center space-x-4">
                                        <div className={`w-2 h-2 rounded-full ${item.type === 'same_day' ? 'bg-red-600 animate-pulse' : item.type === '1_day' ? 'bg-red-500' : 'bg-yellow-500'}`} />
                                        <div>
                                            <p className="font-semibold text-gray-900 dark:text-white">
                                                {item.plan.student.first_name} {item.plan.student.last_name}
                                            </p>
                                            <p className="text-sm text-gray-500">
                                                {new Date(item.installment.date).toLocaleDateString('tr-TR')} - {Number(item.installment.amount).toLocaleString('tr-TR')} {item.plan.currency}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end">
                                        <span className={`px-2 py-1 text-xs font-bold rounded-lg ${item.type === 'same_day'
                                            ? 'bg-red-200 text-red-800 dark:bg-red-900/50 dark:text-red-300'
                                            : item.type === '1_day'
                                                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                                : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                            }`}>
                                            {item.type === 'same_day' ? 'BUGÜN!' : item.type === '1_day' ? '1 Gün Kaldı' : '3 Gün Kaldı'}
                                        </span>
                                        <span className="text-xs text-gray-400 mt-1">
                                            {item.plan.student.parent_phone || 'Tel Yok!'}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="p-6 border-t border-gray-100 dark:border-gray-800 flex justify-end space-x-3 bg-gray-50 dark:bg-gray-800/50 rounded-b-2xl">
                    <button
                        onClick={onClose}
                        disabled={sending}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors font-medium text-sm"
                    >
                        Kapat
                    </button>
                    {reminders.length > 0 && (
                        <button
                            onClick={handleSendAll}
                            disabled={sending}
                            className={`flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg transition-colors font-medium text-sm ${sending ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}
                        >
                            <Send className="w-4 h-4" />
                            <span>{sending ? 'Gönderiliyor...' : 'Tümünü Gönder'}</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PaymentReminderModal;
